import { Layout } from "antd";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { connect, useDispatch, useSelector } from "react-redux";
import { useParams, withRouter } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import { ApiNames, getPharmacyPatientByIdAPI } from "../api/api";
import { TableSection } from "../component/customComponent/TableSection";
import { ToolBar } from "../component/customComponent/ToolBar";
import { OrdersCalendar } from "../component/OrdersCalendar/index";
import { applyFilters } from "../component/OrdersFilter";
import "../component/SavedList/SavedListIcon.css";
import { SavedListTable } from "../component/SavedList/SavedListTable";
import {
  selectPatients,
  selectFilters,
  selectSite,
  selectUser,
  selectIsPhiAllowed,
  selectIsFetching,
  selectNetworkActionsError,
  selectTotalPatients,
  selectCurrentPage,
  selectSorting,
  selectOrdersTableSearchedText,
  selectIsLoadingEnabled,
  selectSavedListData,
  selectSettings
} from "../store/selector";
import { ORDER_VIEW } from "../constant/filters";
import { searchTypes, ViewType } from "../constant/general";
import { Routes } from "../constant/routes";
import ACTIONS from "../store/action";
import ErrorMessage from "../component/customComponent/customMessages/ErrorMessage";
import { isModifierClick } from "../utils/misc";
import { parseUrlFilters, hasUrlFilters } from "../utils/urlParams";

const { Content } = Layout;

function MainViewComponent({ t, history, user, isPhiAllowed, patients, filters, total, currentPage, isFetching }) {
  const dispatch = useDispatch();
  const { viewType } = useParams();
  const queryParameters = new URLSearchParams(window.location.search);
  const patientId = queryParameters.get("patient");
  const pharmacyPatientId = parseInt(queryParameters.get("pharmacyPatient"));
  const sorting = useSelector(selectSorting);
  const searchText = useSelector(selectOrdersTableSearchedText) || "";
  const { selectedPage: savedListCurrentPage } = useSelector(selectSavedListData) || 1;

  const setCurrentPageAct = (page) => dispatch(ACTIONS.setCurrentPage(page));
  const isInitialMount = useRef(true);

  const [filteredPatients, setFilteredPatients] = useState([]);
  const [searchInput, setSearchInput] = useState(searchText);
  const { orderViewFilter: orderViewType } = useSelector(selectFilters);
  const [patientPhiInfoNetworkError] = useSelector(selectNetworkActionsError([ApiNames.PatientPhiInfo]));
  const [isProcessingEmailUrlFilters, setIsProcessingEmailUrlFilters] = useState(hasUrlFilters(queryParameters));
  const isLoadingEnabled = useSelector(selectIsLoadingEnabled);
  const settings = useSelector(selectSettings);
  const NAME_ID = searchTypes.name_id.key;

  const debouncedSearch = useCallback(
    debounce((text) => searchPatientTextFiltersAct(1, text, NAME_ID, filters), 500),
    [filters]
  );

  const setEnableCycleFlashAct = (enableFlash) => dispatch(ACTIONS.setEnableCycleFlash(enableFlash));
  const setPatientAct = (pharmacyPatient, patientId, orderId) =>
    dispatch(ACTIONS.setPatientAction(pharmacyPatient, patientId, orderId));
  const getPatientOrderAct = (pharmacyPatient) => dispatch(ACTIONS.getPatientOrderAction(pharmacyPatient));
  const setCurrentCycle = (currentCycle) => dispatch(ACTIONS.setCurrentCycle(currentCycle));
  const searchPatientTextFiltersAct = (page, text, searchBy, filters) =>
    dispatch(ACTIONS.searchPatientTextFiltersAction(page, text, searchBy, filters));
  const setFiltersAct = (filters, apiName, page, sorting) =>
    dispatch(ACTIONS.setFiltersAction(filters, apiName, page, sorting));
  const setPatientModal = (show, tab) => dispatch(ACTIONS.setShowPatientModal(show, tab));
  const setOrdersTableSearchTextAct = (ordersTableSearchText) =>
    dispatch(ACTIONS.setOrdersTableSearchText(ordersTableSearchText));
  const setViewTypeAct = (viewType) => dispatch(ACTIONS.setViewType(viewType));
  const setCalendarOrderDateFilterACT = (calendarOrderDateFilter) =>
    dispatch(ACTIONS.setCalendarOrderDateFilter(calendarOrderDateFilter));
  const fetchOrders = (page, apiName = ApiNames.Search, sorter) => {
    setFiltersAct(
      {
        ...filters,
        forceCallApi: true
      },
      apiName,
      page,
      sorter
    );
  };
  const searchSavedListAct = (page) => dispatch(ACTIONS.searchSavedListAction(page));
  const setFiltersObjectAct = (filters) => dispatch(ACTIONS.setFilters(filters));

  useEffect(() => {
    const filteredPatients = applyFilters(patients, filters, false);
    if (!(currentPage > 1 && filteredPatients.length < 1 && patients.length > 0)) {
      setFilteredPatients(filteredPatients);
    }
  }, [patients, filters]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setOrdersTableSearchTextAct(searchInput);
    if (searchInput && searchInput.trim().length > 0) {
      setCurrentPageAct(1);
      debouncedSearch(searchInput);
    } else if (!searchInput) {
      debouncedSearch.cancel();
      setOrdersTableSearchTextAct("");
      fetchOrders(currentPage, ApiNames.SearchWithoutRefresh, sorting);
    }
  }, [searchInput]);

  useEffect(() => {
    //NOTE : Don't change the URL if the user enter the app from external source(FNC i.e when patient & pharamcyPatient params are included in the url)
    if (!pharmacyPatientId && !patientId && !hasUrlFilters(queryParameters)) {
      history.push(Routes.ROOT + (viewType || ViewType.List.key));
    }
    setViewTypeAct(viewType);
    if (hasUrlFilters(queryParameters) && settings && Object.keys(settings).length > 0) {
      const replacementYears = settings["fe.daterange-picker-years-lookback"]?.value;
      const urlFilters = parseUrlFilters(queryParameters, replacementYears);
      const updatedFilters = {
        ...filters,
        ...urlFilters,
        forceCallApi: true
      };
      setFiltersObjectAct(updatedFilters);
      setFiltersAct(updatedFilters, ApiNames.Search, currentPage, sorting);
      setIsProcessingEmailUrlFilters(false);
    }
    if (viewType === ViewType.List.key && !hasUrlFilters(queryParameters)) {
      fetchOrders(currentPage, ApiNames.Search, sorting);
    }
    if (viewType === ViewType.SavedList.key) searchSavedListAct(savedListCurrentPage);
  }, [viewType, settings]);

  const getPharmacyPatientById = async (pharmacyPatient) => {
    try {
      const { data } = await getPharmacyPatientByIdAPI(pharmacyPatient);
      return data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.[0]?.message || t("main_view.error_messages.failed_to_get_pharmacy_patient");
      ErrorMessage(errorMessage);
      return null;
    }
  };

  useEffect(async () => {
    if (pharmacyPatientId && patientId) {
      if (isPhiAllowed === undefined) return;

      // Note: for Non-PHI users and falied network to get patient info just replace the URL with the default view (List)
      if (isPhiAllowed === false || patientPhiInfoNetworkError?.code === "Error") {
        patientPhiInfoNetworkError?.code === "Error" && setPatientModal(false, 0);
        history.replace(Routes.ROOT + (viewType || ViewType.List.key));

        return;
      }
      const externalPatient = await getPharmacyPatientById(pharmacyPatientId);
      if (!externalPatient) {
        // If getPharmacyPatientById returns null, redirect to default view
        history.replace(Routes.ROOT + (viewType || ViewType.List.key));
        return;
      }

      externalPatient.pharmacyPatientCurrentOrder = externalPatient.currentOrder.id;

      if (
        orderViewType === ORDER_VIEW.ALL_ORDERS.value &&
        externalPatient.currentOrder.id !== externalPatient.pharmacyPatientCurrentOrder
      ) {
        setPatientModal(true, 1);
        setEnableCycleFlashAct(true);
      } else {
        setPatientModal(true, 0);
      }

      setCurrentCycle(externalPatient);
      setPatientAct(pharmacyPatientId, patientId, externalPatient.currentOrder.id);
    }
  }, [pharmacyPatientId, isPhiAllowed, patientPhiInfoNetworkError?.code]);

  const onViewTypeChange = (e) => {
    const viewType = e.target.value;
    history.push(Routes.ROOT + viewType);
    setViewTypeAct(viewType);
    setCurrentPageAct(1);
    setOrdersTableSearchTextAct("");
    setSearchInput("");
  };

  const onHeaderCell = (column) => ({
    id: `${column.id}-sorting`
  });

  const onClickTable = (modalTabIndex = 0) => ({
    onHeaderCell,
    onCell: (patient) => ({
      onClick: (e) => {
        if (isModifierClick(e)) {
          return;
        }

        e.preventDefault();
        if (
          orderViewType === ORDER_VIEW.ALL_ORDERS.value &&
          modalTabIndex === 0 &&
          patient.currentOrder.id !== patient.pharmacyPatientCurrentOrder
        ) {
          setPatientModal(true, 1);
          setEnableCycleFlashAct(true);
        } else {
          setPatientModal(true, modalTabIndex);
        }
        const { id } = patient;
        setPatientAct(id, patient.patient.id, patient.currentOrder.id);
        getPatientOrderAct(id);
      }
    })
  });

  const onClickCalendar = (patient) => {
    if (
      orderViewType === ORDER_VIEW.ALL_ORDERS.value &&
      patient.currentOrder.id !== patient.pharmacyPatientCurrentOrder
    ) {
      setPatientModal(true, 1);
      setEnableCycleFlashAct(true);
    } else {
      setPatientModal(true, 0);
    }
    const { id } = patient;
    setPatientAct(id, patient.patient.id, patient.currentOrder.id);
    getPatientOrderAct(id);
  };

  return (
    <Content style={{ backgroundColor: "white" }}>
      <ToolBar t={t} user={user} onViewTypeChange={onViewTypeChange} viewType={viewType} padding="16px 50px 0px 50px" />
      {viewType === ViewType.List.key && (
        <TableSection
          setSearchInput={setSearchInput}
          searchInput={searchInput}
          patients={filteredPatients}
          onClickTable={onClickTable}
          onHeaderCell={onHeaderCell}
          total={total}
          currentPage={currentPage}
          isFetching={isFetching || isProcessingEmailUrlFilters}
          fetchOrders={fetchOrders}
          isLoadingEnabled={isLoadingEnabled}
        />
      )}
      {viewType === ViewType.Calendar.key && (
        <OrdersCalendar
          patients={filteredPatients}
          onClickCalendar={onClickCalendar}
          onCalenderViewChange={(fromDate, toDate) => (
            setCalendarOrderDateFilterACT({ fromDate, toDate }), fetchOrders(1, ApiNames.Search, sorting)
          )}
          isFetching={isFetching}
          paddingLeft={35}
        />
      )}
      {viewType === ViewType.SavedList.key && <SavedListTable />}
    </Content>
  );
}

const mapStateToProps = createStructuredSelector({
  patients: selectPatients,
  site: selectSite,
  filters: selectFilters,
  user: selectUser,
  isPhiAllowed: selectIsPhiAllowed,
  isFetching: selectIsFetching([ApiNames.Search]),
  total: selectTotalPatients,
  currentPage: selectCurrentPage
});

const MainView = connect(mapStateToProps)(withRouter(withTranslation()(MainViewComponent)));

export { MainView };
